import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CheckCircle2, Circle, Calendar, BarChart3 } from "lucide-react";

interface AnalyticsProps {
  modulosData: Array<{ 
    modulo_nome: string; 
    categoria_nome: string; 
    total_cards: number; 
    cards_vistos: number; 
    concluido: number;
    data_ultimo_estudo: string;
  }>;
  diagnostico: {
    texto: string;
    tipo: 'success' | 'warning' | 'danger' | 'neutral';
  };
  diarioData?: Array<{ label: string; value: number }>;
  semanalData?: Array<{ label: string; value: number }>;
  mensalData?: Array<{ label: string; value: number }>;
}

export function StudentAnalytics({ modulosData: modulosRecebidos, diagnostico: diagnosticoRecebido, diarioData = [], semanalData = [], mensalData = [] }: AnalyticsProps) {
  // O servidor pode deixar de mandar um dos dois (aluno sem histórico, versão
  // mais antiga): a tela mostra o que há, em vez de quebrar inteira.
  const modulosData = modulosRecebidos ?? [];
  const diagnostico = diagnosticoRecebido ?? { texto: "Ainda não há dados suficientes para um diagnóstico.", tipo: "neutral" as const };
  const [activePeriod, setActivePeriod] = useState<'diario' | 'semanal' | 'mensal'>('diario');

  const getLemaColor = (tipo: string) => {
    switch (tipo) {
      case 'success': return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-400';
      case 'warning': return 'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-400';
      case 'danger': return 'bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-400';
      default: return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  const getChartData = () => {
    switch (activePeriod) {
      case 'semanal': return semanalData;
      case 'mensal': return mensalData;
      default: return diarioData;
    }
  };

  const chartData = getChartData();

  return (
    <div className="space-y-6">
      {/* Lema / Diagnóstico */}
      <div className={`p-4 rounded-xl border ${getLemaColor(diagnostico.tipo)} shadow-sm animate-scale-in`}>
        <h3 className="font-bold mb-1 flex items-center gap-2">
          <span>🎯</span> Diagnóstico de Aprendizado
        </h3>
        <p className="text-sm">{diagnostico.texto}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Histórico de Estudos (Gráficos) */}
        <Card className="lg:col-span-2 shadow-md border-none overflow-hidden">
          <CardHeader className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pb-4 gap-4">
            <div>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Histórico de Cards Estudados
              </CardTitle>
              <p className="text-xs text-muted-foreground">Monitore o seu ritmo de estudos ao longo do tempo</p>
            </div>
            
            <div className="flex bg-muted p-1 rounded-lg self-start sm:self-center">
              {(['diario', 'semanal', 'mensal'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setActivePeriod(period)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                    activePeriod === period
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {period === 'diario' ? 'Diário' : period === 'semanal' ? 'Semanal' : 'Mensal'}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="h-[300px]">
            {chartData && chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="label" 
                    stroke="#94a3b8" 
                    fontSize={12}
                    tickLine={false} 
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#94a3b8" 
                    fontSize={12}
                    tickLine={false} 
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.96)', 
                      borderRadius: '8px', 
                      border: 'none', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                    }}
                    labelStyle={{ fontWeight: 'bold', color: '#0f172a' }}
                    itemStyle={{ color: '#013079' }}
                    formatter={(value: any) => [`${value} cards`, 'Revisões']}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#013079" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={45} 
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground text-sm">Nenhum dado registrado para este período.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Módulos Concluídos */}
        <Card className="shadow-md border-none overflow-hidden">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Módulos Concluídos
            </CardTitle>
            <p className="text-xs text-muted-foreground">Status geral de progresso</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {modulosData && modulosData.length > 0 ? (
                modulosData.map((mod, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border border-muted rounded-xl hover:bg-muted/5 transition-all">
                    <div className="flex items-center gap-3">
                      {mod.concluido ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 fill-emerald-50" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground/30" />
                      )}
                      <div>
                        <p className="font-semibold text-sm text-foreground">{mod.modulo_nome}</p>
                        <p className="text-xs text-muted-foreground">{mod.categoria_nome}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-foreground">
                        {mod.cards_vistos} / {mod.total_cards}
                      </p>
                      <p className="text-xs text-muted-foreground">cards vistos</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center text-sm">Nenhum módulo iniciado ainda.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
